'use client';

// module imports
import Image from 'next/image';
import { useCallback, useRef, useState } from 'react';

// file imports
import axiosInstance from '@/lib/axios';
import { showError, showSuccess } from '@/lib/toast';

// Types
interface ImageUploadProps {
  onUploadSuccess: (url: string, fileName: string) => void;
  onUploadError?: (error: string) => void;
  folder?: string;
  makePublic?: boolean;
  maxSize?: number; // in MB
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  showPreview?: boolean;
  previewSize?: number;
}

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  fileName?: string;
}

export default function ImageUpload({
  onUploadSuccess,
  onUploadError,
  folder = 'images',
  makePublic = true,
  maxSize = 5, // 5MB default for images
  className = '',
  children,
  disabled = false,
  showPreview = true,
  previewSize = 200,
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const validateImage = useCallback(
    (file: File): string | null => {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        return 'Please select an image file';
      }

      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return `Image size must be less than ${maxSize}MB`;
      }

      return null;
    },
    [maxSize]
  );

  const handleImageUpload = useCallback(
    async (file: File) => {
      const validationError = validateImage(file);
      if (validationError) {
        showError(validationError);
        onUploadError?.(validationError);
        return;
      }

      // Create preview
      if (showPreview) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }

      setUploadProgress({
        isUploading: true,
        progress: 0,
        fileName: file.name,
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('makePublic', makePublic.toString());

        const response = await axiosInstance.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prev) => ({ ...prev, progress }));
            }
          },
        });

        if (response.data.success) {
          showSuccess('Image uploaded successfully!');
          onUploadSuccess(response.data.data.url, response.data.data.fileName);
          setPreviewUrl(null); // Clear preview
        } else {
          throw new Error(response.data.error || 'Upload failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
        showError(errorMessage);
        onUploadError?.(errorMessage);
        setPreviewUrl(null); // Clear preview on error
      } finally {
        setUploadProgress({
          isUploading: false,
          progress: 0,
        });
        // Reset input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [folder, makePublic, validateImage, onUploadSuccess, onUploadError, showPreview]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
    },
    [handleImageUpload]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !uploadProgress.isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, uploadProgress.isUploading]);

  return (
    <div className={className}>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={disabled || uploadProgress.isUploading} />

      <div onClick={handleClick} className="cursor-pointer">
        {children || (
          <div
            className={`
              border-2 border-dashed border-gray-300 rounded-lg p-6 text-center
              hover:border-gray-400 transition-colors
              ${disabled || uploadProgress.isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {uploadProgress.isUploading ? (
              <div className="space-y-3">
                <div className="text-sm text-gray-600">Uploading {uploadProgress.fileName}...</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress.progress}%` }} />
                </div>
                <div className="text-xs text-gray-500">{uploadProgress.progress}%</div>
              </div>
            ) : showPreview && previewUrl ? (
              <div className="space-y-3">
                <div className="relative mx-auto" style={{ width: previewSize, height: previewSize }}>
                  <Image src={previewUrl} alt="Preview" width={previewSize} height={previewSize} className="object-cover rounded-lg" />
                </div>
                <div className="text-sm text-gray-600">Click to upload or select another image</div>
                <div className="text-xs text-gray-500">Max size: {maxSize}MB</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-4xl text-gray-400">📷</div>
                <div className="text-gray-600">Click to upload image</div>
                <div className="text-sm text-gray-500">Max size: {maxSize}MB</div>
                <div className="text-xs text-gray-400">Supports: JPG, PNG, GIF, WebP</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
